import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import LandingNavbar from '../components/LandingNavbar';

const videoStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  objectFit: 'cover',
  zIndex: 0,
  filter: 'brightness(0.7)'
};
const overlayStyle = {
  position: 'relative',
  zIndex: 1,
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
};
const heroBox = {
  background: 'rgba(255,255,255,0.10)',
  border: '1.5px solid #fff',
  borderRadius: '18px',
  padding: '2.5rem 2rem',
  marginTop: '2rem',
  maxWidth: 420,
  color: '#fff',
  fontSize: '1.25rem',
  fontWeight: 400,
  textAlign: 'center',
  boxShadow: '0 4px 32px 0 rgba(0,0,0,0.08)'
};
const titleStyle = {
  fontFamily: 'Montserrat, Arial, sans-serif',
  fontWeight: 700,
  fontSize: '2.8rem',
  color: '#fff',
  letterSpacing: '0.04em',
  marginBottom: '0.5rem',
  textShadow: '0 2px 16px rgba(0,0,0,0.18)'
};
const subtitleStyle = {
  fontFamily: '"Dancing Script", "Brush Script MT", cursive',
  fontWeight: 700,
  fontSize: '1.7rem',
  color: '#fff',
  marginBottom: '1.5rem',
  textShadow: '0 2px 16px rgba(0,0,0,0.18)'
};

const testimonials = [
  {
    text: "Karen's voice is like a gentle wave—soothing, grounding, and deeply healing. Every session leaves me feeling lighter and more at peace.",
    name: 'Sophie M.',
    role: 'App user'
  },
  {
    text: "I've never experienced such calm and clarity as I have with Karen's meditations. Her presence is powerful, even through a screen.",
    name: 'James L.',
    role: 'Retreat guest'
  },
  {
    text: "Karen's sound healing sessions are transformative. Her voice carries a warmth and strength that truly helps me heal.",
    name: 'Priya S.',
    role: 'App user'
  },
  {
    text: "The way Karen guides each practice is both calming and empowering. I always finish feeling renewed.",
    name: 'Alex T.',
    role: 'Wellness client'
  }
];

function TestimonialCarousel() {
  const [index, setIndex] = useState(0);
  const timeoutRef = useRef(null);

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearTimeout(timeoutRef.current);
  }, [index]);

  return (
    <div style={{
      margin: '2.5rem auto 0 auto',
      maxWidth: 680,
      background: 'rgba(255,255,255,0.10)',
      border: '1.5px solid #fff',
      borderRadius: 16,
      color: '#fff',
      padding: '1rem 1.5rem',
      boxShadow: '0 2px 16px 0 rgba(0,0,0,0.08)',
      position: 'relative',
      minHeight: 80,
      transition: 'background 0.3s'
    }}>
      <div style={{ fontSize: '1.18rem', fontStyle: 'italic', lineHeight: 1.7, minHeight: 90 }}>
        “{testimonials[index].text}”
      </div>
      <div style={{ marginTop: 18, fontWeight: 600, fontSize: '1.05rem', letterSpacing: '0.01em' }}>
        {testimonials[index].name}
        <span style={{ color: '#e0e0e0', fontWeight: 400, fontSize: '0.98rem', marginLeft: 8 }}>
          {testimonials[index].role ? `· ${testimonials[index].role}` : ''}
        </span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18, gap: 8 }}>
        {testimonials.map((_, i) => (
          <span key={i} style={{
            display: 'inline-block',
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: i === index ? '#fff' : 'rgba(255,255,255,0.25)',
            transition: 'background 0.2s',
            cursor: 'pointer'
          }} onClick={() => setIndex(i)} />
        ))}
      </div>
    </div>
  );
}

function HomeSection() {
  return (
    <div style={{ marginTop: '8vh', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={titleStyle}>Waves Within</div>
      <div style={subtitleStyle}>find your flow state</div>
      <div style={heroBox}>
        Wellbeing for all through<br />
        <span style={{ fontWeight: 600 }}>music | movement | meditation | connection</span>
      </div>
    </div>
  );
}

function WelcomeSection() {
  return (
    <div style={{
      marginTop: '8vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
      minHeight: 420,
      position: 'relative',
    }}>
      <div style={{
        position: 'relative',
        width: '100%',
        maxWidth: 1000,
        minHeight: 260,
        marginTop: '2rem',
        marginLeft: 'auto',
        marginRight: 'auto',
        borderRadius: 14,
        overflow: 'hidden',
        zIndex: 1,
        boxShadow: '0 2px 16px 0 rgba(0,0,0,0.08)',
        display: 'flex',
        justifyContent: 'center',
      }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          background: `linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 60%, rgba(0,0,0,0.05) 100%), url('/karen1.png') left center/cover no-repeat`,
          zIndex: 0,
          filter: 'brightness(0.98)'
        }} />
        <div style={{
          position: 'relative',
          zIndex: 1,
          color: '#fff',
          fontFamily: 'Montserrat, Arial, sans-serif',
          fontWeight: 500,
          fontSize: '1.18rem',
          lineHeight: 1.7,
          textAlign: 'right',
          padding: '2.5rem',
          minWidth: 350,
          maxWidth: 520,
          marginLeft: 'auto',
        }}>
          Hello and welcome to my page. I'm Karen. An avid Yogi, musician & nature lover. Through music, movement, meditation and deep connection to nature through my sea swimming I found a healthy outlet for all the stress and anxieties that had been building up in me over the years. I also found my freedom. Discovering who I truly am under all the layers, labels and pressures of life. Life's experiences and encounters brought me on a huge journey to find myself, my true path, to find my flow state. Healing is not linear. It is always a work in progress. Sharing my journey, and all the learnings and teachings that helped me is why I created Waves Within. We can all be happy & free, if we invest in ourselves and our wellbeing ♥
        </div>
      </div>
      <TestimonialCarousel />
    </div>
  );
}

const offeringCards = [
  { title: 'Yoga', desc: 'Gentle, restorative, and vinyasa classes for all levels.', img: 'https://tidalsoulwellness.com/_assets/media/ada793954a457680583a06272d439d40.png' },
  { title: 'Sound Healing', desc: 'Experience deep relaxation with gongs, bowls, and more.', img: 'https://tidalsoulwellness.com/_assets/media/64c471f3896426f7543159c28fad1101.png' },
  { title: 'Meditation', desc: 'Guided sessions to calm the mind and nurture the spirit.', img: 'https://media.istockphoto.com/id/1459538299/photo/woman-hands-or-lotus-pose-meditation-on-sunset-beach-ocean-or-sea-in-mental-health-mind.jpg?s=2048x2048&w=is&k=20&c=p1Cr95TDyty1gVCVZlDJJT5_COOe1gADnIKaSjCDlHA=' },
  { title: 'Retreats', desc: 'Immersive getaways for transformation and renewal.', img: 'https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?auto=format&fit=crop&w=600&q=80' },
  { title: 'Events', desc: 'Workshops, community gatherings, and special events.', img: 'https://images.unsplash.com/photo-1464983953574-0892a716854b?auto=format&fit=crop&w=400&q=80' },
  { title: 'Corporate Bookings', desc: 'Bring wellness to your workplace with custom programs.', img: 'https://images.unsplash.com/photo-1512436991641-6745cdb1723f?auto=format&fit=crop&w=400&q=80' }
];

const therapyCards = [
  { title: 'Sound Healing', img: 'https://tidalsoulwellness.com/_assets/media/64c471f3896426f7543159c28fad1101.png' },
  { title: 'EFT', img: 'https://media.istockphoto.com/id/166152874/photo/eft-tapping-points.jpg?s=612x612&w=0&k=20&c=6jNFT7gXIafrNVBoWVeGwED1zNtKmIIsO-OdCylHTG0=' }
];
const therapyDetails = [
  'Sound Healing: The therapeutic application of sound frequencies. a safe, gentle yet very effective way to bring balance, harmony and healing to your body, mind and spirit',
  'EFT: Emotional Freedom Technique. Music can heal the wounds that medicine cannot touch'
];

function OfferingsSection() {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <div style={{ marginTop: '8vh', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <h2 style={{ color: '#fff', fontFamily: 'Montserrat, Arial, sans-serif', fontWeight: 700 }}>Our Offerings</h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', justifyContent: 'center', marginTop: '2rem' }}>
        {offeringCards.map((card, idx) => (
          <div key={card.title} style={{
            background: `url(${card.img}) center/cover no-repeat`,
            border: '1.5px solid #fff',
            borderRadius: 14,
            color: '#fff',
            width: 210,
            minHeight: 120,
            padding: '1.5rem 1rem',
            cursor: 'pointer',
            boxShadow: '0 2px 16px 0 rgba(0,0,0,0.08)',
            textAlign: 'center',
            fontWeight: 600,
            fontSize: '1.15rem',
            transition: 'background 0.2s, border 0.2s',
            outline: openIdx === idx ? '2px solid #fff' : 'none',
            opacity: openIdx === null || openIdx === idx ? 1 : 0.7,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            position: 'relative',
            backgroundBlendMode: 'darken',
            backgroundColor: 'rgba(0,0,0,0.18)'
          }} onClick={() => setOpenIdx(openIdx === idx ? null : idx)}>
            <span style={{
              background: 'rgba(0,0,0,0.32)',
              borderRadius: 8,
              padding: '0.5rem 1rem',
              width: '100%'
            }}>{card.title}</span>
          </div>
        ))}
      </div>
      {openIdx !== null && (
        <div style={{
          marginTop: '2.5rem',
          background: 'rgba(0,0,0,0.18)',
          borderRadius: 12,
          color: '#fff',
          maxWidth: 600,
          padding: '2rem 2.5rem',
          fontSize: '1.13rem',
          textAlign: 'center',
          boxShadow: '0 2px 16px 0 rgba(0,0,0,0.08)'
        }}>
          <strong>{offeringCards[openIdx].title}</strong><br />
          {offeringCards[openIdx].desc}<br />
          <span style={{ color: '#e0e0e0', fontSize: '0.98rem' }}>Insert more details here.</span>
        </div>
      )}
    </div>
  );
}

const sectionBg = {
  minHeight: '100vh',
  width: '100vw',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  zIndex: 1,
};

// Add stock images for events and store
const eventImages = [
  'https://media.istockphoto.com/id/963863844/photo/young-woman-practicing-yoga-on-the-beach.jpg?s=2048x2048&w=is&k=20&c=ZjtiKahH7mFfizn38Wu9UfWyls6Ng9Og3iZMld6Wc9U=', // sunrise yoga
  'https://media.istockphoto.com/id/1284197828/photo/ait-benhaddou-kasbah-berber-sunrise-or-sunset-view.jpg?s=2048x2048&w=is&k=20&c=mM-Ooi3UMxHbaCwLNPAXh0e-TgjYqeaBA4GDxzsLfbc='  // Morocco
];
const storeImages = [
  'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=2420&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  // Yoga Class
  'https://media.istockphoto.com/id/1459538299/photo/woman-hands-or-lotus-pose-meditation-on-sunset-beach-ocean-or-sea-in-mental-health-mind.jpg?s=2048x2048&w=is&k=20&c=p1Cr95TDyty1gVCVZlDJJT5_COOe1gADnIKaSjCDlHA=',
  // Meditation Class
  'https://images.unsplash.com/photo-1593811167562-9cef47bfc4d7?q=80&w=2344&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  // Sound Healing Session
  'https://media.istockphoto.com/id/454147973/photo/historic-legal-text-document-script.jpg?s=2048x2048&w=is&k=20&c=QqssH908UtvV4dRXvVZfOjujTqkoAnYVsKIRkTDXbXQ=',
  // Personalised Mantra (writing/journal theme)
  'https://media.istockphoto.com/id/1049891208/photo/online-shopping.jpg?s=2048x2048&w=is&k=20&c=aMpirW3aLrZkYf3L1AW4jIMpuJ6mMRsHZX1VfKaDi3g=',
];

function EventsSection() {
  const [openIdx, setOpenIdx] = useState(null);
  const events = [
    { title: 'Summer Solstice Yoga', img: eventImages[0] },
    { title: 'Retreat to Morocco', img: eventImages[1] }
  ];
  const eventDetails = [
    'Celebrate the longest day of the year with sunrise yoga on the beach. Lorum ipsum dolor sit amet, consectetur adipiscing elit. Pellentesque euismod, nisi eu consectetur.',
    'Join us for a transformative retreat in Morocco. Lorum ipsum dolor sit amet, consectetur adipiscing elit. Pellentesque euismod, nisi eu consectetur.'
  ];
  return (
    <div style={{ marginTop: '8vh', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <h2 style={{ color: '#fff', fontFamily: 'Montserrat, Arial, sans-serif', fontWeight: 700 }}>Upcoming Events</h2>
      <div style={{ display: 'flex', gap: '2rem', justifyContent: 'center', marginTop: '2rem', flexWrap: 'wrap' }}>
        {events.map((event, idx) => (
          <div key={event.title} style={{
            background: `url(${event.img}) center/cover no-repeat`,
            border: '1.5px solid #fff',
            borderRadius: 14,
            color: '#fff',
            width: 260,
            minHeight: 160,
            padding: '1.5rem 1rem',
            cursor: 'pointer',
            boxShadow: '0 2px 16px 0 rgba(0,0,0,0.08)',
            textAlign: 'center',
            fontWeight: 600,
            fontSize: '1.15rem',
            transition: 'box-shadow 0.2s',
            outline: openIdx === idx ? '2px solid #fff' : 'none',
            opacity: openIdx === null || openIdx === idx ? 1 : 0.7,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            position: 'relative',
            backgroundBlendMode: 'darken',
            backgroundColor: 'rgba(0,0,0,0.18)'
          }} onClick={() => setOpenIdx(openIdx === idx ? null : idx)}>
            <span style={{
              background: 'rgba(0,0,0,0.32)',
              borderRadius: 8,
              padding: '0.5rem 1rem',
              width: '100%'
            }}>{event.title}</span>
          </div>
        ))}
      </div>
      {openIdx !== null && (
        <div style={{
          marginTop: '2.5rem',
          background: 'rgba(0,0,0,0.18)',
          borderRadius: 12,
          color: '#fff',
          maxWidth: 600,
          padding: '2rem 2.5rem',
          fontSize: '1.13rem',
          textAlign: 'center',
          boxShadow: '0 2px 16px 0 rgba(0,0,0,0.08)'
        }}>
          <strong>{events[openIdx].title}</strong><br />
          {eventDetails[openIdx]}
        </div>
      )}
    </div>
  );
}

function TherapiesSection() {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <div style={{ marginTop: '8vh', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <h2 style={{ color: '#fff', fontFamily: 'Montserrat, Arial, sans-serif', fontWeight: 700 }}>Therapies</h2>
      <div style={{ display: 'flex', gap: '2rem', justifyContent: 'center', marginTop: '2rem', flexWrap: 'wrap' }}>
        {therapyCards.map((therapy, idx) => (
          <div key={therapy.title} style={{
            background: `url(${therapy.img}) center/cover no-repeat`,
            border: '1.5px solid #fff',
            borderRadius: 14,
            color: '#fff',
            width: 260,
            minHeight: 120,
            padding: '1.5rem 1rem',
            cursor: 'pointer',
            boxShadow: '0 2px 16px 0 rgba(0,0,0,0.08)',
            textAlign: 'center',
            fontWeight: 600,
            fontSize: '1.15rem',
            transition: 'box-shadow 0.2s',
            outline: openIdx === idx ? '2px solid #fff' : 'none',
            opacity: openIdx === null || openIdx === idx ? 1 : 0.7,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            position: 'relative',
            backgroundBlendMode: 'darken',
            backgroundColor: 'rgba(0,0,0,0.18)'
          }} onClick={() => setOpenIdx(openIdx === idx ? null : idx)}>
            <span style={{
              background: 'rgba(0,0,0,0.32)',
              borderRadius: 8,
              padding: '0.5rem 1rem',
              width: '100%'
            }}>{therapy.title}</span>
          </div>
        ))}
      </div>
      {openIdx !== null && (
        <div style={{
          marginTop: '2.5rem',
          background: 'rgba(255,255,255,0.10)',
          border: '1.5px solid #fff',
          borderRadius: 14,
          color: '#fff',
          maxWidth: 700,
          display: 'flex',
          alignItems: 'center',
          boxShadow: '0 2px 16px 0 rgba(0,0,0,0.08)'
        }}>
          <img src={therapyCards[openIdx].img} alt={therapyCards[openIdx].title} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 12, margin: '1.5rem' }} />
          <div style={{ flex: 1, padding: '1.5rem 1rem', fontSize: '1.13rem' }}>
            <strong style={{ fontSize: '1.2rem' }}>{therapyCards[openIdx].title}</strong>
            <div style={{ marginTop: 8 }}>
              {therapyDetails[openIdx]}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StoreSection() {
  const products = [
    { title: 'Yoga Class', img: storeImages[0] },
    { title: 'Meditation Class', img: storeImages[1] },
    { title: 'Sound Healing Session', img: storeImages[2] },
    { title: 'Personalised Mantra', img: storeImages[3] },
    { title: 'T-shirt', img: storeImages[4] }
  ];
  return (
    <div style={{ marginTop: '8vh', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ width: '100%', maxWidth: 900, marginTop: '2rem' }}>
        {products.map((product, idx) => (
          <div key={product.title} style={{
            display: 'flex', alignItems: 'center',
            background: 'rgba(255,255,255,0.10)',
            border: '1.5px solid #fff',
            borderRadius: 14,
            color: '#fff',
            marginBottom: '2rem',
            boxShadow: '0 2px 16px 0 rgba(0,0,0,0.08)'
          }}>
            <img src={product.img} alt={product.title} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 12, margin: '1.5rem' }} />
            <div style={{ flex: 1, padding: '1.5rem 1rem' }}>
              <strong style={{ fontSize: '1.2rem' }}>{product.title}</strong>
              <div style={{ marginTop: 8, fontSize: '1.08rem' }}>
                Lorum ipsum dolor sit amet, consectetur adipiscing elit. Pellentesque euismod, nisi eu consectetur.
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ContactSection() {
  return (
    <div style={{ marginTop: '8vh', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <h2 style={{ color: '#fff', fontFamily: 'Montserrat, Arial, sans-serif', fontWeight: 700 }}>Contact Me</h2>
      <div style={{
        background: 'rgba(255,255,255,0.10)',
        border: '1.5px solid #fff',
        borderRadius: 14,
        color: '#fff',
        maxWidth: 500,
        padding: '2.5rem 2rem',
        fontSize: '1.13rem',
        textAlign: 'center',
        boxShadow: '0 2px 16px 0 rgba(0,0,0,0.08)',
        marginTop: '2rem'
      }}>
        <div style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: 16 }}>
          Ready to find your flow state?<br />
          <span style={{ fontWeight: 400, fontSize: '1.1rem' }}>Reach out and start your journey today!</span>
        </div>
        <div style={{ marginBottom: 18 }}>
          <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" style={{ color: '#fff', margin: '0 1rem', textDecoration: 'none', fontSize: 22 }}>
            Facebook
          </a>
          <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" style={{ color: '#fff', margin: '0 1rem', textDecoration: 'none', fontSize: 22 }}>
            Instagram
          </a>
        </div>
        <div style={{ marginBottom: 10 }}>
          <strong>Email:</strong> <a href="mailto:hello@tidalsoulwellness.com" style={{ color: '#fff', textDecoration: 'underline' }}>hello@tidalsoulwellness.com</a>
        </div>
        <div style={{ marginBottom: 10 }}>
          <strong>Phone:</strong> <a href="tel:+1234567890" style={{ color: '#fff', textDecoration: 'underline' }}>+1 234 567 890</a>
        </div>
      </div>
    </div>
  );
}

function AppSection({ setActiveTab }) {
  const navigate = useNavigate();
  
  return (
    <div style={{ marginTop: '8vh', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <h2 style={{ color: '#fff', fontFamily: 'Montserrat, Arial, sans-serif', fontWeight: 700 }}>Waves Within App – Your Wellness, Anywhere</h2>
      
      <div style={{
        background: 'rgba(255,255,255,0.10)',
        border: '1.5px solid #fff',
        borderRadius: 14,
        color: '#fff',
        maxWidth: 800,
        padding: '2.5rem',
        marginTop: '2rem',
        textAlign: 'center',
        boxShadow: '0 2px 16px 0 rgba(0,0,0,0.08)'
      }}>
        <p style={{ fontSize: '1.2rem', lineHeight: 1.6, marginBottom: '2rem' }}>
          A digital sanctuary inspired by my in-person work, bringing the essence of wellness into your daily life. 
          Experience the same calming presence and transformative practices wherever you are, whenever you need them.
        </p>
        
        <div style={{ textAlign: 'left', maxWidth: 600, margin: '0 auto 2rem auto' }}>
          <ul style={{ fontSize: '1.1rem', lineHeight: 1.8, listStyle: 'none', padding: 0 }}>
            <li style={{ marginBottom: '0.8rem', display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '12px', fontSize: '1.2rem' }}>✨</span>
              Daily mantras - Daily mantras to help you stay grounded and focused.
            </li>
            <li style={{ marginBottom: '0.8rem', display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '12px', fontSize: '1.2rem' }}>🌙</span>
              Sleep journeys - Guided meditations to help you drift into peaceful sleep.
            </li>
            <li style={{ marginBottom: '0.8rem', display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '12px', fontSize: '1.2rem' }}>🎵</span>
              Personalised sound healing - Personalised mantras and sound healing sessions to help you heal and transform.
            </li>
            <li style={{ marginBottom: '0.8rem', display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '12px', fontSize: '1.2rem' }}>📝</span>
              Journal space - A space to write your thoughts and feelings.
            </li>
            <li style={{ marginBottom: '0.8rem', display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '12px', fontSize: '1.2rem' }}>💎</span>
              Free and premium access
            </li>
          </ul>
        </div>
        
        <button style={{
          background: 'rgba(255,255,255,0.15)',
          border: '2px solid #fff',
          borderRadius: 8,
          color: '#fff',
          padding: '1rem 2rem',
          fontSize: '1.1rem',
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'all 0.2s',
          fontFamily: 'Montserrat, Arial, sans-serif'
        }} onMouseOver={(e) => {
          e.target.style.background = 'rgba(255,255,255,0.25)';
          e.target.style.transform = 'translateY(-2px)';
        }} onMouseOut={(e) => {
          e.target.style.background = 'rgba(255,255,255,0.15)';
          e.target.style.transform = 'translateY(0)';
        }} onClick={() => navigate('/login')}>
          Find your flow
        </button>
      </div>
    </div>
  );
}

const LandingPage = () => {
  const [activeTab, setActiveTab] = useState('Home');

  const tabs = ['Home', 'Welcome', 'Offerings', 'Events', 'Therapies', 'App', 'Store', 'Contact'];

  const renderContent = () => {
    switch (activeTab) {
      case 'Home':
        return <HomeSection />;
      case 'Welcome':
        return <WelcomeSection />;
      case 'Offerings':
        return <OfferingsSection />;
      case 'Events':
        return <EventsSection />;
      case 'Therapies':
        return <TherapiesSection />;
      case 'App':
        return <AppSection setActiveTab={setActiveTab} />;
      case 'Store':
        return <StoreSection />;
      case 'Contact':
        return <ContactSection />;
      default:
        return <HomeSection />;
    }
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden' }}>
      <video autoPlay loop muted playsInline style={videoStyle}>
        <source src="/loginwaves.mp4" type="video/mp4" />
      </video>
      <LandingNavbar section={activeTab} setSection={setActiveTab} />
      <div style={overlayStyle}>
        <div style={sectionBg}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default LandingPage; 